import React from "react";
import { Link } from "react-router-dom";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { SITE_URL } from "@/utils/seo";
import { CheckCircle, ArrowRight, Truck, Shield, Clock } from "lucide-react";

const SITE = SITE_URL;

const strollers = [
  { slug: "baby-jogger-city-mini-gt2-single", name: "Baby Jogger City Mini GT2 Single", type: "Single", price: 22 },
  { slug: "bob-single-stroller", name: "BOB Single Stroller", type: "Single", price: 24 },
  { slug: "uppababy-vista-v2", name: "UPPAbaby VISTA V2", type: "Single", price: 26 },
  { slug: "baby-jogger-city-mini-gt2-double", name: "Baby Jogger City Mini GT2 Double", type: "Double", price: 34 },
  { slug: "bob-double-stroller", name: "BOB Double Stroller", type: "Double", price: 36 },
  { slug: "uppababy-cruz-v2", name: "UPPAbaby Cruz V2", type: "Single", price: 24 },
];

const faqs = [
  {
    q: "Do you deliver strollers to Walt Disney World resorts?",
    a: "Yes. We deliver to every Walt Disney World resort. Delivery and pickup require an in-person lobby meeting — we do not leave strollers unattended. We offer 1-hour delivery windows from 7 am to 10 pm."
  },
  {
    q: "Does Disney allow stroller delivery to bell services?",
    a: "Disney does not permit third-party rental vendors to leave equipment with bell services. Every delivery and pickup at a Disney resort requires an in-person meeting in the lobby."
  },
  {
    q: "Can I use the stroller on Disney buses, the Monorail and the Skyliner?",
    a: "Yes, folded strollers are allowed on Disney buses, the Monorail and the Skyliner. You will need to fold the stroller before boarding. All of our single strollers fold quickly and compactly."
  },
  {
    q: "What is the Disney stroller size rule?",
    a: "Walt Disney World requires strollers to be no larger than 31 inches wide and 52 inches long. All of our strollers meet these requirements. Note: Disney has separate rental strollers inside the parks, but many guests prefer a fully reclining stroller that stays with them all day."
  },
  {
    q: "Is delivery free?",
    a: "Standard delivery is free to Disney resorts, Universal Orlando, most Orlando hotels, vacation homes and Airbnb properties. No early-delivery surcharges."
  },
  {
    q: "How far in advance should I book?",
    a: "We recommend booking as soon as your Disney trip is confirmed. Spring break, summer and holiday weeks fill quickly. Bookings can be made weeks or months ahead."
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Strollers", item: `${SITE}/strollers` },
    { "@type": "ListItem", position: 3, name: "Disney World Stroller Rentals", item: `${SITE}/disney-world-stroller-rentals` },
  ],
};

export default function DisneyWorldStrollers() {
  return (
    <>
      <PageSEO
        title="Disney World Stroller Rentals | Free Delivery to Your Resort"
        description="Rent a premium stroller for your Walt Disney World vacation. Free delivery to every Disney resort with in-person lobby service. Baby Jogger, BOB, UPPAbaby, Nuna and more from $20/day."
        canonical={`${SITE}/disney-world-stroller-rentals`}
        structuredData={[breadcrumbSchema, faqSchema]}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "#040A0F" }}>
        {/* Hero */}
        <section className="pt-28 pb-16 text-center" style={{ background: "linear-gradient(180deg, #060f18 0%, #040A0F 100%)" }}>
          <div className="container max-w-3xl">
            <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-4">
              <Link to="/" className="hover:text-[#FFD166]">Home</Link>
              <span className="mx-2 opacity-40">/</span>
              <Link to="/strollers" className="hover:text-[#FFD166]">Strollers</Link>
              <span className="mx-2 opacity-40">/</span>
              <span className="text-[#FFD166]">Disney World Stroller Rentals</span>
            </nav>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F6F1E4] mb-5">
              Disney World Stroller Rentals
            </h1>
            <p className="text-[#B9C8D5] text-lg max-w-2xl mx-auto">
              Premium single and double strollers delivered directly to your Walt Disney World resort. Enchanted Gear Rentals provides free delivery with in-person lobby service — the way Disney resort delivery should work.
            </p>
            <p className="mt-3 text-sm text-[#B9C8D5] italic">Not affiliated with The Walt Disney Company.</p>
            <div className="mt-7 flex gap-4 justify-center flex-wrap">
              <Link to="/#trip-setup"><button className="btn-primary" data-testid="disney-cta-top">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
              <Link to="/strollers"><button className="btn-ghost">Browse All Strollers</button></Link>
            </div>
          </div>
        </section>

        {/* Trust signals */}
        <section className="container py-10">
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              { Icon: Truck, title: "Free resort delivery", body: "We deliver to every Walt Disney World resort. No hidden fees, no bell-service drop-offs — we meet you in person." },
              { Icon: Shield, title: "Hospital-grade clean", body: "Every stroller is sanitized between rentals. Your family gets a clean, inspected stroller every time." },
              { Icon: Clock, title: "Flexible windows", body: "1-hour delivery windows available from 7 am to 10 pm — pick the exact hour that fits your park schedule." },
            ].map(({ Icon, title, body }) => (
              <div key={title} className="rounded-xl bg-[#0A1825] border border-[#FFD166]/15 p-6">
                <Icon className="w-6 h-6 text-[#FFD166] mb-3" />
                <h2 className="font-serif text-lg text-[#F6F1E4] mb-2">{title}</h2>
                <p className="text-[#B9C8D5] text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How delivery works */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-6">How Disney resort delivery works</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-5">
              {[
                { step: "1", title: "Reserve online", body: "Select your dates, delivery location and stroller model. We confirm availability instantly." },
                { step: "2", title: "Choose a delivery window", body: "Pick any 1-hour window from 7 am to 10 pm — choose the hour that fits your arrival schedule." },
                { step: "3", title: "In-person lobby meeting", body: "Disney does not allow third-party vendors to leave equipment with bell services. We meet you in the resort lobby to hand off your stroller directly." },
                { step: "4", title: "Pickup on your way out", body: "We meet you again in the lobby for pickup at the end of your rental. Need a different time? Just contact us." },
              ].map(({ step, title, body }) => (
                <div key={step} className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#FFD166]/15 border border-[#FFD166]/30 flex items-center justify-center text-[#FFD166] font-bold text-sm shrink-0">{step}</div>
                  <div>
                    <div className="font-semibold text-[#F6F1E4]">{title}</div>
                    <div className="text-sm text-[#B9C8D5] mt-1">{body}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-xl bg-[#0A1825] border border-[#FFD166]/15 p-6">
              <h3 className="font-serif text-lg text-[#FFD166] mb-4">Disney stroller size rules</h3>
              <p className="text-[#B9C8D5] text-sm mb-4">Walt Disney World requires park strollers to be no larger than 31 inches wide and 52 inches long. All of our strollers meet this requirement.</p>
              <div className="space-y-2">
                {[
                  "Fold the stroller before boarding Disney buses",
                  "Strollers allowed folded on the Skyliner and Monorail",
                  "Stroller parking areas at every park entrance",
                  "Tag your stroller — it may be moved by park staff",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2 text-sm text-[#F6F1E4]">
                    <CheckCircle className="w-4 h-4 text-[#FFD166] shrink-0 mt-0.5" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Product recommendations */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-2">Recommended strollers for Disney World</h2>
          <p className="text-[#B9C8D5] mb-8">All models meet Disney's size requirements, fold easily for transportation and recline for nap time.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {strollers.map((s) => (
              <Link key={s.slug} to={`/strollers/${s.slug}`} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 hover:border-[#FFD166]/40 p-5 transition-all hover:-translate-y-1">
                <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5] border border-[#FFD166]/20 px-2 py-0.5 rounded-full w-fit mb-2">{s.type}</div>
                <div className="font-serif text-lg text-[#F6F1E4] group-hover:text-[#FFD166] transition-colors">{s.name}</div>
                <div className="text-[#FFD166] font-semibold mt-2">From ${s.price}/day</div>
                <div className="text-xs text-[#B9C8D5] mt-1 flex items-center gap-1">View details <ArrowRight className="w-3 h-3" /></div>
              </Link>
            ))}
          </div>
          <div className="mt-6 text-center">
            <Link to="/strollers"><button className="btn-ghost">See full catalog</button></Link>
          </div>
        </section>

        {/* FAQ */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-8">Frequently asked questions</h2>
          <div className="space-y-4 max-w-3xl">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 p-5">
                <summary className="font-semibold text-[#F6F1E4] cursor-pointer list-none flex justify-between items-center gap-3">
                  {f.q}
                  <span className="text-[#FFD166] text-xl leading-none shrink-0 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-[#B9C8D5] text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="container pb-20 text-center">
          <div className="max-w-xl mx-auto rounded-2xl bg-[#0A1825] border border-[#FFD166]/20 p-8">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-3">Ready for your Disney World trip?</h2>
            <p className="text-[#B9C8D5] mb-6">Select your dates and delivery location to check availability and confirm your stroller rental.</p>
            <Link to="/#trip-setup"><button className="btn-primary" data-testid="disney-cta-bottom">Reserve Your Stroller <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
